import React, { Component } from "react";
import ReactTooltip from "react-tooltip";

import { IEPG, IEPGProgram } from "./Timeline/IEPG";
import { Readjusted } from "./Timeline/Readjusted";
import { Time } from "./Timeline/Time";
import { PlayerProps } from "./properties";

import styles from "./Timeline.module.css";

export interface TimelineProps {
    // Current time
    currentTime: Date;
    // Global player properties
    playerProps: PlayerProps;

    // When player reaches end
    onEndReach?(): void;
    // When setting player start (launch play at position)
    onSetStart?(start: Date): void;
    updateZoom(zoom: number): void;
}

// Ideal with for one big step
const IDEAL_STEP_WIDTH = 160;

interface TimelineState {
    declarativeEPG: IEPGProgram[];
    selectionEnd: Date | null;
    selectionLeft: number;
    selectionStart: Date | null;
    selectionWidth: number;
    musicEPG: IEPGProgram[];
    readjustedEPG: IEPGProgram[];
    readjustedEPG2: IEPGProgram[];
    viewport: number;
    zoom: number;
}

export class Timeline extends Component<TimelineProps, TimelineState> {
    private container: HTMLDivElement | null = null;
    private containerCheckInterval: number = 0;
    private dragModeEnabled: boolean = false;
    private hoverCursor: HTMLElement | null = null;
    private lastContainerWidth: number = 0;
    private ready: boolean = false;
    private selectionDraggingMode: string = "";
    private tooltip: HTMLElement | null;
    private tooltipId = "";
    private tooltipText = "";

    constructor(props: TimelineProps) {
        super(props);
        this.tooltipId = `timeTooltip_${
            Math.random().toString(36).substring(2, 15) +
            Math.random().toString(36).substring(2, 15)
        }`;
        this.state = {
            declarativeEPG: [],
            musicEPG: [],
            readjustedEPG: [],
            readjustedEPG2: [],
            selectionEnd:
                typeof this.props.playerProps.selectionEnd === "undefined"
                    ? null
                    : this.props.playerProps.selectionEnd,
            selectionLeft: -1,
            selectionStart:
                typeof this.props.playerProps.selectionStart === "undefined"
                    ? null
                    : this.props.playerProps.selectionStart,
            selectionWidth: -1,
            viewport: 0,
            zoom: 1,
        };
        if (typeof this.props.playerProps.onSelectionChange !== "undefined") {
            this.dragModeEnabled = true;
        }
    }

    public componentDidMount() {
        if (this.containerCheckInterval !== 0) {
            window.clearInterval(this.containerCheckInterval);
            this.containerCheckInterval = 0;
        }
        if (this.container === null) {
            return;
        }
        this.lastContainerWidth = this.container.getBoundingClientRect().width;
        this.setState({ viewport: this.lastContainerWidth });
        this.containerCheckInterval = window.setInterval(() => {
            if (this.container === null) {
                return;
            }
            const viewport = this.container.getBoundingClientRect().width;
            if (this.lastContainerWidth !== viewport) {
                this.setState({ viewport: viewport });
            }
            this.lastContainerWidth = viewport;
        }, 1000);
        window.setTimeout(() => {
            this.refreshSelection();
            this.getEPGs();
            this.ready = true;
        }, 1000);
    }

    public componentWillUnmount() {
        if (this.containerCheckInterval !== 0) {
            window.clearInterval(this.containerCheckInterval);
            this.containerCheckInterval = 0;
        }
        this.lastContainerWidth = 0;
    }

    public render() {
        let minHeight = 25 + 20 + 9;

        if (this.state.declarativeEPG.length > 0) {
            minHeight += 25;
        }
        if (this.state.readjustedEPG.length > 0) {
            minHeight += 25;
        }
        if (this.state.readjustedEPG2.length > 0) {
            minHeight += 25;
        }
        if (this.state.musicEPG.length > 0) {
            minHeight += 25;
        }
        return (
            <div
                className={styles.container}
                onMouseDown={this.onMouseDown.bind(this)}
                onMouseLeave={this.onMouseLeave.bind(this)}
                onMouseMove={this.onMouseMove.bind(this)}
                onMouseUp={this.onMouseUp.bind(this)}
                ref={(e) => {
                    this.container = e;
                }}
                style={{
                    minHeight,
                }}
            >
                <ReactTooltip
                    className={styles.tooltip}
                    getContent={() => this.tooltipText}
                    id={this.tooltipId}
                    overridePosition={this.overrideTooltipPosition.bind(this)}
                />
                <div className={styles.dragLine}>
                    <p />
                </div>
                <div
                    className={styles.lines}
                    data-for={this.tooltipId}
                    data-html={true}
                    data-tip=""
                    onClick={this.onTimeClick.bind(this)}
                    style={{
                        width:
                            this.getTimelineDuration() /
                            this.computeSecondsByPixel(),
                    }}
                    onMouseLeave={this.onMouseLinesLeave.bind(this)}
                    onMouseMove={this.onMouseLinesMove.bind(this)}
                >
                    <div
                        className={styles.cursor}
                        style={{
                            left: this.getCursorX(),
                        }}
                    />
                    <div
                        className={styles.hoverCursor}
                        ref={(c) => (this.hoverCursor = c)}
                    />

                    {this.state.selectionLeft >= 0 &&
                    this.state.selectionWidth >= 0 ? (
                        <div
                            className={styles.selection}
                            style={{
                                left: this.state.selectionLeft,
                                width: this.state.selectionWidth,
                            }}
                        />
                    ) : (
                        ""
                    )}
                    {this.state.selectionLeft >= 0 &&
                    this.state.selectionWidth >= 0 ? (
                        <div
                            className={
                                styles.selectionStart +
                                (this.dragModeEnabled
                                    ? " " + styles.selectionDrag
                                    : "")
                            }
                            data-type="selectionStart"
                            style={{
                                left: this.state.selectionLeft,
                            }}
                        />
                    ) : (
                        ""
                    )}
                    {this.state.selectionLeft >= 0 &&
                    this.state.selectionWidth >= 0 ? (
                        <div
                            className={
                                styles.selectionEnd +
                                (this.dragModeEnabled
                                    ? " " + styles.selectionDrag
                                    : "")
                            }
                            data-type="selectionEnd"
                            style={{
                                left:
                                    this.state.selectionLeft +
                                    this.state.selectionWidth,
                            }}
                        />
                    ) : (
                        ""
                    )}

                    <Time
                        secondsByPixel={this.computeSecondsByPixel()}
                        tickRanges={this.computeRanges()}
                        timelineDuration={this.getTimelineDuration()}
                        timelineStart={this.getTimelineStart()}
                    />
                    {this.state.readjustedEPG.length > 0 ? (
                        <Readjusted
                            epg={this.state.readjustedEPG}
                            onSetStart={this.props.onSetStart}
                            tickRanges={this.computeRanges()}
                            timelineDuration={this.getTimelineDuration()}
                            timelineStart={this.getTimelineStart()}
                        />
                    ) : (
                        ""
                    )}
                    {this.state.readjustedEPG2.length > 0 ? (
                        <Readjusted
                            epg={this.state.readjustedEPG2}
                            onSetStart={this.props.onSetStart}
                            tickRanges={this.computeRanges()}
                            timelineDuration={this.getTimelineDuration()}
                            timelineStart={this.getTimelineStart()}
                        />
                    ) : (
                        ""
                    )}
                    {this.state.musicEPG.length > 0 ? (
                        <Readjusted
                            epg={this.state.musicEPG}
                            onSetStart={this.props.onSetStart}
                            tickRanges={this.computeRanges()}
                            timelineDuration={this.getTimelineDuration()}
                            timelineStart={this.getTimelineStart()}
                        />
                    ) : (
                        ""
                    )}
                </div>
            </div>
        );
    }

    public setViewport(viewport: number): void {
        this.setState({ viewport: viewport });
        this.refreshSelection();
    }

    public center(): void {
        if (this.container === null) {
            return;
        }
        const left = this.getCursorX() - this.state.viewport / 2;
        this.container.scrollLeft = left < 0 ? 0 : left;
    }

    public setZoom(zoom: number): void {
        this.setState({ zoom: zoom });
        this.props.updateZoom(zoom);
        this.refreshSelection();
        this.center();
    }

    private getCursorX(): number {
        const x =
            (this.props.currentTime.getTime() -
                this.getTimelineStart().getTime()) /
            1000 /
            this.computeSecondsByPixel();

        if (this.ready && typeof this.props.onEndReach !== "undefined") {
            const end =
                this.getTimelineStart().getTime() +
                this.getTimelineDuration() * 1000;
            if (this.props.currentTime.getTime() >= end) {
                this.props.onEndReach();
            }
        }
        return x;
    }

    private getTimelineStart(): Date {
        const start = Math.floor(
            (this.props.playerProps.start.getTime() -
                (this.getTimelineDuration() * 1000) / 2) /
                1000
        );
        return new Date(start * 1000);
    }

    private getTimelineDuration(): number {
        if (
            typeof this.props.playerProps.timelineDuration === "undefined" ||
            this.props.playerProps.timelineDuration < 60 ||
            this.props.playerProps.timelineDuration > 86400
        ) {
            return 86400;
        }
        return Math.round(this.props.playerProps.timelineDuration);
    }

    private computeRanges() {
        const spx = this.computeSecondsByPixel();
        const ranges = Time.getRanges();

        let range = ranges[ranges.length - 1];

        ranges.some((testedRange: number[]) => {
            if (testedRange[0] / spx >= IDEAL_STEP_WIDTH) {
                range = testedRange;
                return true;
            }
            return false;
        });
        return range;
    }

    private computeSecondsByPixel(): number {
        return (
            this.getTimelineDuration() / (this.state.viewport * this.state.zoom)
        );
    }

    private getEPGs() {
        if (typeof this.props.playerProps.apiKey === "undefined") {
            return;
        }
        const http = new XMLHttpRequest();
        const url = "https://secure-stream.yacast.fr/rest/v1/getEPG";

        http.open("POST", url, true);

        http.setRequestHeader(
            "Authorization",
            `Bearer ${this.props.playerProps.apiKey}`
        );
        http.setRequestHeader("Content-Type", "application/json");

        http.onreadystatechange = () => {
            if (http.readyState === 4 && http.status === 200) {
                const res: IEPG[] = JSON.parse(http.responseText);

                if (!Array.isArray(res)) {
                    console.warn("Invalid EPG");
                    return;
                }
                const state = { ...this.state };
                res.forEach((e: IEPG) => {
                    switch (e.type) {
                        case 0:
                            state.declarativeEPG = e.programs;
                            break;
                        case 1:
                            state.readjustedEPG = e.programs;
                            break;
                        case 2:
                            state.readjustedEPG2 = e.programs;
                            break;
                        case 3:
                            state.musicEPG = e.programs;
                            break;
                    }
                });
                this.setState(state);
            }
        };

        const data = {
            audience: false,
            declarative: false,
            end:
                Math.ceil(this.getTimelineStart().getTime() / 1000) +
                this.getTimelineDuration() +
                3 * 60 * 60,
            media: this.props.playerProps.mediaID,
            music: this.props.playerProps.timelineHaveMusicEPG === true,
            readjusted:
                this.props.playerProps.timelineHaveReadjustedEPG === true,
            start:
                Math.floor(this.getTimelineStart().getTime() / 1000) -
                3 * 60 * 60,
        };
        http.send(JSON.stringify(data));
    }

    public updateSelection = (start: Date, end: Date) => {
        this.setState(
            { selectionStart: start, selectionEnd: end },
            this.refreshSelection
        );
    };

    private refreshSelection(notify: boolean = false) {
        if (
            this.state.selectionStart === null ||
            this.state.selectionEnd === null
        ) {
            return;
        }
        const selectionLeft =
            (this.state.selectionStart.getTime() -
                this.getTimelineStart().getTime()) /
            1000 /
            this.computeSecondsByPixel();
        const selectionWidth =
            (this.state.selectionEnd.getTime() -
                this.state.selectionStart.getTime()) /
            1000 /
            this.computeSecondsByPixel();
        this.setState({
            selectionLeft: selectionLeft,
            selectionWidth: selectionWidth,
        });
        if (
            notify &&
            typeof this.props.playerProps.onSelectionChange !== "undefined"
        ) {
            this.props.playerProps.onSelectionChange(
                this.state.selectionStart,
                this.state.selectionEnd
            );
        }
    }

    private onTimeClick(ev: MouseEvent): void {
        if (typeof this.props.onSetStart === "undefined") {
            return;
        }
        const bound = (ev.currentTarget as HTMLElement).getBoundingClientRect();
        const ts =
            this.getTimelineStart().getTime() +
            ((this.getTimelineDuration() * (ev.pageX - bound.x)) /
                this.getWidth()) *
                1000;

        this.props.onSetStart(new Date(Math.floor(ts / 1000) * 1000));
    }

    private getWidth(): number {
        return this.getTimelineDuration() / this.computeSecondsByPixel();
    }

    private onMouseLinesLeave(): void {
        if (this.hoverCursor === null) {
            return;
        }
        this.hoverCursor.style.display = "none";
        this.hoverCursor.style.left = "0px";
    }

    private onMouseLinesMove(ev: MouseEvent): void {
        if (ev.currentTarget === null) {
            return;
        }
        if (this.tooltip !== null) {
            ReactTooltip.show(this.tooltip as any);
        }
        const bound = (ev.currentTarget as HTMLElement).getBoundingClientRect();
        const time = new Date(
            this.getTimelineStart().getTime() +
                this.getTimelineDuration() *
                    ((ev.pageX - bound.x) / this.getWidth()) *
                    1000
        );
        const x =
            (time.getTime() - this.getTimelineStart().getTime()) /
            1000 /
            this.computeSecondsByPixel();
        this.tooltipText = this.getTimeString(time);
        if (this.hoverCursor !== null) {
            this.hoverCursor.style.display = "block";
            this.hoverCursor.style.left = `${x}px`;
        }
    }

    private overrideTooltipPosition(position: {
        left: number;
        top: number;
    }): { left: number; top: number } {
        const pos = position;

        if (this.container !== null) {
            pos.top = this.container.offsetTop - 3;
        }
        return pos;
    }

    private getTimeString(date: Date): string {
        let res = "";
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const seconds = date.getSeconds();
        const ms = date.getMilliseconds();

        res += (hours < 10 ? "0" : "") + hours;
        res += ":";
        res += (minutes < 10 ? "0" : "") + minutes;
        res += ":";
        res += (seconds < 10 ? "0" : "") + seconds;
        res += ".";
        res += (ms < 10 ? "00" : ms < 100 ? "0" : "") + ms;
        return res;
    }

    private onMouseDown(ev: MouseEvent): void {
        const type = (ev.target as HTMLElement).getAttribute("data-type");

        if (
            !this.dragModeEnabled ||
            (type !== "selectionStart" && type !== "selectionEnd")
        ) {
            return;
        }
        ev.preventDefault();
        ev.stopPropagation();
        this.selectionDraggingMode = type;
    }

    private onMouseLeave(): void {
        this.selectionDraggingMode = "";
    }

    private onMouseMove(ev: MouseEvent): void {
        if (ev.currentTarget !== null && this.selectionDraggingMode !== "") {
            const container = ev.currentTarget as HTMLElement;
            const x =
                container.scrollLeft +
                (ev.clientX - container.getBoundingClientRect().x);
            const ratio = x / container.scrollWidth;
            const date = new Date(
                this.getTimelineStart().getTime() +
                    this.getTimelineDuration() * 1000 * ratio
            );
            let state = this.state;

            state =
                this.selectionDraggingMode === "selectionStart"
                    ? { ...state, selectionStart: date }
                    : { ...state, selectionEnd: date };
            this.state = state;
            this.refreshSelection(true);
        }
    }

    private onMouseUp(): void {
        this.selectionDraggingMode = "";
    }
}
